import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
const TOKEN_STORAGE_KEY = 'pos-auth-token'

// const fetchUserProfile = async (userId: string) => {
//   try {
//     const { data, error } = await supabase
//       .from('profiles')
//       .select('*')
//       .eq('id', userId)
//       .single()

//     if (error) {
//       console.error('Error fetching user profile:', error)
//       return
//     }

//     setUser({
//       id: data.id,
//       email: data.email,
//       role: data.role || 'cashier',
//       name: data.name || data.email,
//       avatar_url: data.avatar_url
//     })
//   } catch (error) {
//     console.error('Error fetching user profile:', error)
//   }
// }

interface User {
  id: string
  email: string
  role: string
  name: string
  avatar_url?: string
  roles?: Array<{ id: string; name: string; description?: string; start_page?: string | null }>
  permissions?: Array<{ id: string; name: string; description?: string; category: string; resource: string; action: string }>
}

interface SessionData {
  userId: string
  email: string
  expiresAt: number
  rememberMe: boolean
  lastActivity: number
}

interface AuthContextType {
  user: User | null
  loading: boolean
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: any }>
  signOut: () => Promise<void>
  signUp: (email: string, password: string, userData: any) => Promise<{ error: any }>
  hasPermission: (permissionName: string) => boolean
  hasAnyPermission: (permissionNames: string[]) => boolean
  sessionExpiresAt: number | null
  updateActivity: () => void
  sessionWarningTime: number | null
  extendSession: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

// Session configuration
const SESSION_DURATION = 8 * 60 * 60 * 1000 // 8 hours in milliseconds
const REMEMBER_ME_DURATION = 30 * 24 * 60 * 60 * 1000 // 30 days in milliseconds
const INACTIVITY_TIMEOUT = 30 * 60 * 1000 // 30 minutes in milliseconds
const SESSION_WARNING_TIME = 5 * 60 * 1000 // 5 minutes before expiration

const SESSION_STORAGE_KEY = 'pos-session'
const DEMO_USER_KEY = 'demo-user'

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null)
  const [lastActivity, setLastActivity] = useState<number>(Date.now())
  const [sessionWarningTime, setSessionWarningTime] = useState<number | null>(null)

  // Load session from storage (check localStorage first so session is shared across tabs)
  const loadSession = (): SessionData | null => {
    try {
      let sessionData = localStorage.getItem(SESSION_STORAGE_KEY) || sessionStorage.getItem(SESSION_STORAGE_KEY)
      if (sessionData) {
        const session: SessionData = JSON.parse(sessionData)
        // Check if session is still valid
        if (session.expiresAt > Date.now()) {
          // Migrate to localStorage if it was only in sessionStorage (so new tabs see the session)
          if (!localStorage.getItem(SESSION_STORAGE_KEY)) {
            localStorage.setItem(SESSION_STORAGE_KEY, sessionData)
          }
          return session
        } else {
          // Session expired, clear it from both storages
          sessionStorage.removeItem(SESSION_STORAGE_KEY)
          localStorage.removeItem(SESSION_STORAGE_KEY)
        }
      }
    } catch (error) {
      console.error('Error loading session:', error)
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
      localStorage.removeItem(SESSION_STORAGE_KEY)
    }
    return null
  }

  // Save session to storage (always use localStorage so session is shared across tabs/windows)
  const saveSession = (userId: string, email: string, rememberMe: boolean) => {
    const now = Date.now()
    const expiresAt = rememberMe 
      ? now + REMEMBER_ME_DURATION 
      : now + SESSION_DURATION
    
    const sessionData: SessionData = {
      userId,
      email,
      expiresAt,
      rememberMe,
      lastActivity: now
    }
    
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData))
    setSessionExpiresAt(expiresAt)
    setLastActivity(now)
  }

  // Clear session
  const clearSession = () => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
    localStorage.removeItem(SESSION_STORAGE_KEY)
    sessionStorage.removeItem(TOKEN_STORAGE_KEY)
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    localStorage.removeItem(DEMO_USER_KEY)
    setSessionExpiresAt(null)
    setLastActivity(Date.now())
  }

  // Update activity timestamp
  const updateActivity = () => {
    const now = Date.now()
    setLastActivity(now)
    
    // Update session activity if exists (always persist to localStorage for cross-tab consistency)
    try {
      const sessionData = localStorage.getItem(SESSION_STORAGE_KEY) || sessionStorage.getItem(SESSION_STORAGE_KEY)
      if (sessionData) {
        const session: SessionData = JSON.parse(sessionData)
        session.lastActivity = now
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
      }
    } catch (error) {
      console.error('Error updating activity:', error)
    }
  }

  // Check session expiration
  useEffect(() => {
    if (!user || !sessionExpiresAt) {
      setSessionWarningTime(null)
      return
    }

    const checkExpiration = () => {
      const now = Date.now()
      const timeUntilExpiration = sessionExpiresAt - now
      
      // Check if session expired
      if (timeUntilExpiration <= 0) {
        handleSessionExpired()
        return
      }
      
      // Check inactivity timeout
      const inactivityTime = now - lastActivity
      if (inactivityTime > INACTIVITY_TIMEOUT) {
        handleInactivityTimeout()
        return
      }
      
      // Show warning if session is about to expire
      if (timeUntilExpiration <= SESSION_WARNING_TIME) {
        if (!sessionWarningTime || Math.abs(sessionWarningTime - timeUntilExpiration) > 1000) {
          showSessionWarning(timeUntilExpiration)
        }
      } else {
        // Clear warning if time remaining is more than warning threshold
        if (sessionWarningTime !== null) {
          setSessionWarningTime(null)
        }
      }
    }

    const interval = setInterval(checkExpiration, 1000) // Check every second for accurate countdown
    checkExpiration() // Initial check

    return () => clearInterval(interval)
  }, [user, sessionExpiresAt, lastActivity, sessionWarningTime])

  // Track user activity
  useEffect(() => {
    if (!user) return

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    const handleActivity = () => updateActivity()

    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true })
    })

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
    }
  }, [user])

  const handleSessionExpired = () => {
    clearSession()
    setUser(null)
    setSessionWarningTime(null)
    // Trigger logout
    if (window.location.pathname !== '/login') {
      window.location.href = '/login?expired=true'
    }
  }

  const handleInactivityTimeout = () => {
    clearSession()
    setUser(null)
    setSessionWarningTime(null)
    // Trigger logout
    if (window.location.pathname !== '/login') {
      window.location.href = '/login?inactive=true'
    }
  }

  const showSessionWarning = (timeUntilExpiration: number) => {
    setSessionWarningTime(timeUntilExpiration)
  }

  const extendSession = () => {
    if (user) {
      const sessionData = sessionStorage.getItem(SESSION_STORAGE_KEY) || localStorage.getItem(SESSION_STORAGE_KEY)
      if (sessionData) {
        const session: SessionData = JSON.parse(sessionData)
        saveSession(session.userId, session.email, session.rememberMe)
        setSessionWarningTime(null)
      }
    }
  }

  // Verify token and fetch user profile
  const verifyTokenAndFetchUser = async (token: string, session: SessionData) => {
    // Fetch user profile - if token is invalid, the API will return an error
    await fetchUserProfile(session.userId, token)
  }

  useEffect(() => {
    // Check for saved session and token (localStorage first so new tabs see the same session)
    const savedSession = loadSession()
    let savedToken = localStorage.getItem(TOKEN_STORAGE_KEY) || sessionStorage.getItem(TOKEN_STORAGE_KEY)
    // Migrate token to localStorage if only in sessionStorage (so new tabs see the session)
    if (savedToken && !localStorage.getItem(TOKEN_STORAGE_KEY)) {
      localStorage.setItem(TOKEN_STORAGE_KEY, savedToken)
    }
    
    if (savedSession && savedToken) {
      // Verify token and fetch user profile
      verifyTokenAndFetchUser(savedToken, savedSession)
    } else {
      // Check for demo user in localStorage (legacy support)
      const demoUser = localStorage.getItem(DEMO_USER_KEY)
      if (demoUser) {
        const userData = JSON.parse(demoUser)
        setUser(userData)
        // Create session for demo user
        saveSession(userData.id, userData.email, false)
        setLoading(false)
      } else {
        // No session, user needs to login
        setUser(null)
        setLoading(false)
      }
    }
  }, [])

  const fetchUserProfile = async (userId: string, token: string) => {
    try {
      // Fetch user with profile and roles from API
      const response = await fetch(`${API_URL}/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        // If unauthorized, token is invalid - clear session
        if (response.status === 401 || response.status === 403) {
          clearSession()
          setUser(null)
          setLoading(false)
          return
        }
        throw new Error('Failed to fetch user profile')
      }

      const data = await response.json()
      
      if (data.success && data.data) {
        const userData = {
          id: data.data.id,
          email: data.data.email,
          role: data.data.role || 'cashier',
          name: data.data.name || data.data.email,
          avatar_url: data.data.avatar_url,
          roles: data.data.roles || [],
          permissions: data.data.permissions || [],
          default_floor_plan_id: data.data.default_floor_plan_id || null
        }
        setUser(userData)
        
        // Refresh session in storage (localStorage for cross-tab persistence)
        const sessionData = localStorage.getItem(SESSION_STORAGE_KEY) || sessionStorage.getItem(SESSION_STORAGE_KEY)
        if (sessionData) {
          const session: SessionData = JSON.parse(sessionData)
          saveSession(data.data.id, data.data.email, session.rememberMe)
        } else {
          saveSession(data.data.id, data.data.email, false)
        }
      } else {
        // Invalid response, clear session
        clearSession()
        setUser(null)
      }
    } catch (error) {
      console.error('Error fetching user profile:', error)
      // Clear session on error
      clearSession()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const hasPermission = (permissionName: string): boolean => {
    if (!user) return false
    // Demo users (for testing) - allow all
    if (user.id?.startsWith('demo-')) {
      return true
    }
    // Super Admin and Admin have all permissions
    if (user.roles?.some(r => r.name === 'Super Admin' || r.name === 'Admin')) {
      return true
    }
    // Check if user has the specific permission
    return user.permissions?.some(p => p.name === permissionName) || false
  }

  const hasAnyPermission = (permissionNames: string[]): boolean => {
    if (!user) return false
    // Demo users (for testing) - allow all
    if (user.id?.startsWith('demo-')) {
      return true
    }
    // Super Admin and Admin have all permissions
    if (user.roles?.some(r => r.name === 'Super Admin' || r.name === 'Admin')) {
      return true
    }
    // Check if user has any of the specified permissions
    return permissionNames.some(perm => user.permissions?.some(p => p.name === perm)) || false
  }

  const signIn = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      // Call backend login API
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      })

      const data = await response.json()

      if (data.success && data.user && data.token) {
        // Save token in localStorage so session is shared across tabs/windows
        localStorage.setItem(TOKEN_STORAGE_KEY, data.token)

        // Save session (always in localStorage for cross-tab persistence)
        saveSession(data.user.id, email, rememberMe)

        // Set user
        setUser({
          id: data.user.id,
          email: data.user.email,
          role: data.user.role || 'cashier',
          name: data.user.name || data.user.email,
          avatar_url: data.user.avatar_url,
          roles: data.user.roles || [],
          permissions: data.user.permissions || []
        })

        return { error: null }
      } else {
        return { error: new Error(data.error || 'Login failed') }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { error: error instanceof Error ? error : new Error('Login failed') }
    }
  }

  const signOut = async () => {
    // Remove token
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    sessionStorage.removeItem(TOKEN_STORAGE_KEY)
    
    clearSession()
    setUser(null)
    setSessionWarningTime(null)
  }

  const signUp = async (email: string, password: string, userData: any) => {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, ...userData })
      })

      const data = await response.json()

      if (data.success) {
        return { error: null }
      } else {
        return { error: new Error(data.error || 'Registration failed') }
      }
    } catch (error) {
      console.error('Signup error:', error)
      return { error: error instanceof Error ? error : new Error('Registration failed') }
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      signIn,
      signOut,
      signUp,
      hasPermission,
      hasAnyPermission,
      sessionExpiresAt,
      updateActivity,
      sessionWarningTime,
      extendSession
    }}>
      {children}
    </AuthContext.Provider>
  )
}
